import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchLeadsData, fetchAgents, fetchLeadSources, fetchLeadStatuses } from "@/lib/apiService";

const LeadsDataPage = () => {
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [agents, setAgents] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);

  const [filters, setFilters] = useState({
    agentName: "all",
    leadSource: "all",
    leadStatus: "all",
    searchTerm: "", // Changed from searchQuery to match API
    limit: 20,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasMore: false,
    lastEvaluatedKey: null,
    pageKeys: [null], // Add array to track keys for each page
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [agentsData, sourcesData, statusesData] = await Promise.all([
          fetchAgents(),
          fetchLeadSources(),
          fetchLeadStatuses()
        ]);

        console.log('Agents:', agentsData);
        console.log('Sources:', sourcesData);
        console.log('Statuses:', statusesData);

        setAgents(agentsData || []);
        setLeadSources(sourcesData || []);
        setLeadStatuses(statusesData || []);

        await loadLeadsData();
      } catch (err) {
        setError("Failed to load initial data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const loadLeadsData = async (lastKey = null, resetPage = true) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchLeadsData(
        filters,
        pagination.currentPage,
        filters.limit,
        lastKey
      );

      const sortedData = (response.leads || []).sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      );

      setLeadsData(sortedData);

      setPagination(prev => ({
        ...prev,
        currentPage: resetPage ? 1 : prev.currentPage,
        totalPages: Math.ceil(response.count / filters.limit) || 1,
        hasMore: response.hasMore,
        lastEvaluatedKey: response.lastEvaluatedKey,
        pageKeys: resetPage
          ? [null] // Reset keys on filter change
          : prev.currentPage >= prev.pageKeys.length // Only add if moving forward
            ? [...prev.pageKeys, response.lastEvaluatedKey]
            : prev.pageKeys
      }));
    } catch (err) {
      setError("Failed to load leads data: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      searchTerm: e.target.value
    }));
  };

  const applyFilters = () => {
    loadLeadsData(null, true);
  };

  const resetFilters = () => {
    setFilters({
      agentName: "all",
      leadSource: "all",
      leadStatus: "all",
      searchTerm: "",
      limit: 20,
    });
    loadLeadsData(null, true);
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      loadLeadsData(pagination.lastEvaluatedKey, false);
      setPagination(prev => ({
        ...prev,
        currentPage: prev.currentPage + 1,
      }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      const prevPageIndex = pagination.currentPage - 2; // -2 because currentPage is 1-based
      const prevKey = pagination.pageKeys[prevPageIndex] || null;
      loadLeadsData(prevKey, false);
      setPagination(prev => ({
        ...prev,
        currentPage: prev.currentPage - 1,
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return "bg-blue-100 text-blue-800";
      case 'contacted': return "bg-yellow-100 text-yellow-800";
      case 'qualified': return "bg-green-100 text-green-800";
      case 'closed': return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Leads Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="space-y-2">
              <label htmlFor="agent-filter" className="text-sm font-medium">Agent</label>
              <Select
                value={filters.agentName}
                onValueChange={(value) => handleFilterChange("agentName", value)}
              >
                <SelectTrigger id="agent-filter">
                  <SelectValue placeholder="Select Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents
                    .filter(agent => agent?.id && agent?.name) // Ensure id and name exist
                    .map((agent) => (
                      <SelectItem key={agent.id} value={agent.name}>
                        {agent.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="source-filter" className="text-sm font-medium">Lead Source</label>
              <Select
                value={filters.leadSource}
                onValueChange={(value) => handleFilterChange("leadSource", value)}
              >
                <SelectTrigger id="source-filter">
                  <SelectValue placeholder="Select Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {leadSources
                    .filter(source => source?.id && source?.name)
                    .map((source) => (
                      <SelectItem key={source.id} value={source.name}>
                        {source.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="status-filter" className="text-sm font-medium">Lead Status</label>
              <Select
                value={filters.leadStatus}
                onValueChange={(value) => handleFilterChange("leadStatus", value)}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {leadStatuses
                    .filter(status => status?.id && status?.name)
                    .map((status) => (
                      <SelectItem key={status.id} value={status.name}>
                        {status.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="search" className="text-sm font-medium">Search</label>
              <Input
                id="search"
                placeholder="Search by name, email, phone..."
                value={filters.searchTerm}
                onChange={handleSearchChange}
                onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-6">
            <Button onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outline" onClick={resetFilters}>Reset Filters</Button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading leads...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadsData.length > 0 ? (
                      leadsData.map((lead) => (
                        <TableRow key={lead.id || `${lead.phoneNumber}-${lead.timestamp}`}>
                          <TableCell className="font-medium">{lead.Name || '-'}</TableCell>
                          <TableCell>{lead.Email || '-'}</TableCell>
                          <TableCell>{lead.phoneNumber || lead.Mobile || '-'}</TableCell>
                          <TableCell>{lead.LeadSource || '-'}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeColor(lead.LeadStatus)}`}>
                              {lead.LeadStatus || '-'}
                            </span>
                          </TableCell>
                          <TableCell>{lead.agentName || '-'}</TableCell>
                          <TableCell>{formatDate(lead.timestamp)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6">
                          No leads found matching the current filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>


              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Page {pagination.currentPage} {pagination.hasMore ? '...' : `of ${pagination.totalPages}`}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={pagination.currentPage === 1} // This remains correct
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!pagination.hasMore}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>

            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadsDataPage;